
import { defineComponent, PropType } from "vue";
import {
  IDanhMucVatTuResponseBase,
  ITaiKhoanKeToanResponse,
} from "@/core/interfaces/ApiResponses";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { withListPage } from "@/core/helpers/list-page-context";
import {
  swalNotification,
  swalErrNotification,
} from "@/core/helpers/utils";
import { usePagination } from "@/core/hooks/use-pagination";

import Setting from "@/components/shared/setting/Setting.vue";
import type { SettingAction } from "@/components/shared/setting/Setting.vue";
import DanhMucVatTuService from "@/core/services/DanhMucVatTu.service";
import ConfirmationDialog from "@/components/confirmation-dialog/ConfirmationDialog.vue";
import WordBreakColumn from "@/components/shared/work-break-column/WordBreakColumn.vue";
import IconEye from "@/icons/IconEye.vue";
import IconPencil from "@/icons/IconPencil.vue";
import IconTrash from "@/icons/IconTrash.vue";

export default defineComponent({
  name: "danh-muc-vat-tu-table",

  components: {
    ConfirmationDialog,
    Setting,
    WordBreakColumn,
  },

  emits: ["delete-danh-muc-vat-tu-success"],

  data() {
    return {
      deletingDanhMucVatTu: null as IDanhMucVatTuResponseBase | null,
      removeItemDialogDisplay: false,
    };
  },

  setup() {
    const { push } = useRouterCustom();

    const { indexCal } = usePagination();

    return {
      push,
      indexCal,
    };
  },

  props: {
    curPage: {
      type: Number,
      default: 0,
    },
    items: Array as PropType<Array<IDanhMucVatTuResponseBase>>,
    isReadonly: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 0,
    },
    showDeleteButton: {
      type: Boolean,
      default: false,
    },
    showNhomChaColumn: {
      type: Boolean,
      default: false,
    },
    /** When set (update page), highlight the row for the item being edited */
    selectedId: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    width(): number {
      if (this.isReadonly) return 60;

      let defaultWidth = 100;

      if (this.showDeleteButton) {
        defaultWidth += 50;
      }

      return defaultWidth;
    },
  },

  methods: {
    rowClassName({ row }: { row: IDanhMucVatTuResponseBase }): string {
      if (this.selectedId > 0 && row.id === this.selectedId) {
        return "current-row";
      }

      return "";
    },

    formatTkKetoan(tk?: ITaiKhoanKeToanResponse | null): string {
      if (!tk?.matk && !tk?.mact && !tk?.tentk) {
        return "-";
      }

      return `${tk?.matk || "-"} / ${tk?.mact || "-"} / ${tk?.tentk || "-"}`;
    },

    getSettingActions(row: IDanhMucVatTuResponseBase): SettingAction[] {
      const actions: SettingAction[] = [
        {
          icon: IconEye,
          label: "Chi tiết",
          tooltip: "Chi tiết nhóm v/tư",
          onClick: () =>
            this.push(`/quan-ly/danh-muc-vat-tu/chi-tiet/${row.id}`),
        },
        {
          icon: IconPencil,
          label: "Chỉnh sửa",
          tooltip: "Chỉnh sửa nhóm v/tư",
          any: ["master:danhmucvattu:sua"],
          onClick: () =>
            this.push(
              withListPage(
                `/quan-ly/danh-muc-vat-tu/cap-nhat/${row.id}`,
                this.curPage
              )
            ),
        },
      ];

      if (this.showDeleteButton) {
        actions.push({
          icon: IconTrash,
          label: "Xóa",
          tooltip: "Xóa nhóm v/tư",
          any: ["master:danhmucvattu:xoa"],
          onClick: () => this.onDeleteButtonClicked(row.id),
        });
      }

      return actions;
    },

    onDeleteButtonClicked(danhMucVatTuId: number) {
      const danhMucVatTu = this.items?.find(
        (item) => item.id === danhMucVatTuId
      );
      this.removeItemDialogDisplay = true;
      if (danhMucVatTu) {
        this.deletingDanhMucVatTu = danhMucVatTu;
      }
    },

    async confirmDelete() {
      this.removeItemDialogDisplay = false;
      try {
        if (this.deletingDanhMucVatTu) {
          await DanhMucVatTuService.delete(this.deletingDanhMucVatTu?.id);
        }
        await swalNotification("Xóa thành công", "success");
        this.$emit("delete-danh-muc-vat-tu-success");
      } catch (error) {
        await swalErrNotification(
          error,
          "Có lỗi xảy ra, không thể xóa Nhóm vật tư này"
        );
      }
    },
  },
});
